import React, { useEffect, useState } from "react";
import { Check, ArrowRight, Loader2, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AuthDialog from "@/components/AuthDialog";

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [interval, setInterval] = useState("month");
  const [authOpen, setAuthOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/api/billing/plans").then(({ data }) => {
      setPlans(data.plans);
      setPacks(data.packs);
    });
  }, []);

  const checkout = async (lookupKey) => {
    if (!user) { setAuthOpen(true); return; }
    setLoadingKey(lookupKey);
    try {
      const { data } = await api.post("/api/billing/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoadingKey(null);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-4 md:pt-8 pb-16 md:pb-24">
      <div className="max-w-2xl mb-16">
        <div className="eyebrow text-muted-foreground mb-4">Pricing</div>
        <h1 className="hero-title text-5xl lg:text-6xl mb-4">Plans built<br/>for scale.</h1>
        <p className="text-muted-foreground max-w-lg">Start with 25 permanent links for free. Upgrade any time — or top up with a one-time lifetime pack.</p>
      </div>

      {/* Interval toggle */}
      <div className="mb-10 flex justify-center">
        <div className="inline-flex border border-border p-1 bg-secondary" data-testid="pricing-interval-toggle">
          <button
            data-testid="pricing-monthly-btn"
            onClick={() => setInterval("month")}
            className={`px-6 py-2 text-sm font-medium transition-all ${interval === "month" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >Monthly</button>
          <button
            data-testid="pricing-annual-btn"
            onClick={() => setInterval("year")}
            className={`px-6 py-2 text-sm font-medium transition-all ${interval === "year" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
          >Annual <span className="text-xs opacity-70 ml-1">(save ~17%)</span></button>
        </div>
      </div>

      {/* 5-tier plan table */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-l border-t border-border" data-testid="pricing-grid">
        {plans.map((p) => {
          const price = interval === "month" ? p.price_monthly : p.price_annual;
          const lookup = interval === "month" ? p.lookup_key_monthly : p.lookup_key_annual;
          const isEnterprise = p.id === "enterprise";
          const isFree = p.id === "free";
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`border-r border-b border-border p-6 flex flex-col ${p.popular ? "bg-secondary/40" : ""} relative`}
            >
              {p.popular && (
                <div className="absolute -top-px left-6 right-6 h-px bg-accent" />
              )}
              <div className="mb-6">
                {p.popular && <div className="eyebrow text-accent mb-2">Most popular</div>}
                <div className="font-heading text-2xl font-light tracking-tight">{p.name}</div>
              </div>
              <div className="mb-8">
                {isEnterprise ? (
                  <div className="font-heading text-4xl font-light">Custom</div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-5xl font-light tracking-tighter">${price}</span>
                      <span className="text-xs text-muted-foreground font-mono">/{interval === "month" ? "mo" : "yr"}</span>
                    </div>
                    {p.quota > 0 && <div className="mt-2 text-xs font-mono text-muted-foreground">{p.quota.toLocaleString()} links</div>}
                  </>
                )}
              </div>
              <ul className="space-y-2.5 text-sm mb-8 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-foreground" />
                    <span className="text-xs md:text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              {isEnterprise ? (
                <Button
                  data-testid={`plan-${p.id}-cta`}
                  onClick={() => setContactOpen(true)}
                  className="w-full rounded-none border-foreground bg-foreground text-background hover:bg-foreground/90"
                >Contact sales</Button>
              ) : isFree ? (
                <Button
                  data-testid={`plan-${p.id}-cta`}
                  variant="outline"
                  onClick={() => user ? window.location.href = "/dashboard" : setAuthOpen(true)}
                  className="w-full rounded-none border-border"
                >{user ? "Go to dashboard" : "Sign up free"}</Button>
              ) : (
                <Button
                  data-testid={`plan-${p.id}-cta`}
                  onClick={() => checkout(lookup)}
                  disabled={loadingKey === lookup}
                  className={`w-full rounded-none ${p.popular ? "bg-foreground text-background hover:bg-foreground/90" : "border border-foreground bg-background text-foreground hover:bg-foreground hover:text-background"}`}
                >
                  {loadingKey === lookup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Upgrade
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Lifetime packs — inverted section */}
      <div className="mt-20 border border-foreground bg-foreground text-background p-8 md:p-12">
        <div className="grid md:grid-cols-3 gap-8 items-start mb-10">
          <div className="md:col-span-2">
            <div className="eyebrow opacity-60 mb-3">One-time · Stackable · Never expire</div>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter">Lifetime permanent link packs.</h2>
          </div>
          <p className="text-sm opacity-70 md:col-span-1">Pay once. Consumed after your plan quota. Stackable across any subscription — great for one-off campaigns.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {packs.map((pk) => (
            <div key={pk.id} data-testid={`pack-${pk.id}`} className="border border-background/30 p-5 flex flex-col hover:bg-background/10 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 opacity-60" />
                <div className="text-xs opacity-70 font-mono">Pack {pk.size}</div>
              </div>
              <div className="font-heading text-3xl tracking-tighter mt-2">${pk.price}</div>
              <div className="text-xs opacity-70 mt-1">{pk.size} links · permanent</div>
              <button
                data-testid={`pack-${pk.id}-buy`}
                onClick={() => checkout(pk.lookup_key)}
                disabled={loadingKey === pk.lookup_key}
                className="mt-6 py-2 text-xs border border-background bg-background text-foreground hover:opacity-90 transition-opacity font-medium disabled:opacity-50 flex items-center justify-center"
              >
                {loadingKey === pk.lookup_key ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Buy pack <ArrowRight className="w-3 h-3 ml-1" /></>}
              </button>
            </div>
          ))}
        </div>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}

function ContactSalesDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/api/billing/enterprise-contact", form);
      toast.success("We'll reach out within 1 business day.");
      onOpenChange(false);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (e) {
      toast.error("Failed to submit");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border border-border sm:max-w-md p-0" data-testid="enterprise-dialog">
        <DialogHeader className="p-6 border-b border-border">
          <div className="eyebrow text-muted-foreground mb-2">Enterprise</div>
          <DialogTitle className="font-heading text-xl font-light">Talk to sales</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">Custom volume, SLA, invoicing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-3">
          <Input data-testid="enterprise-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          <Input data-testid="enterprise-email" required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" />
          <Input data-testid="enterprise-company" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none" />
          <Textarea data-testid="enterprise-message" required rows={4} placeholder="What are you building?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none" />
          <Button type="submit" data-testid="enterprise-submit" disabled={sending} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Send
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
